import { draftBatchHeader, markBatchDrafted, recordingAdapter, setBatchDraftedHandler, shouldRecord } from "@/modules/approval/draft-batch"; // TKP-237, TKP-242
import i18n from "@/modules/localization/i18n";
import Vue from "vue";
import { BACKEND_URL } from "./index";

import axios from "axios";
import { entityFromDraftResponse } from "@/modules/approval/approval"; // TKP-104
import { noteDraft } from "@/modules/approval/draft-notice"; // TKP-241 p.4

import NProgress from "vue-nprogress";
import store from "../store/store";

Vue.use(NProgress, {
  router: true, // Show progressbar when navigating routes, default: true
  http: false, // Show progressbar when doing Vue.http, default: true
});

const nprogress = new NProgress();

axios.defaults.baseURL = BACKEND_URL;
axios.defaults.withCredentials = true;

let isRefreshing = false;
let refreshPromise;

axios.interceptors.request.use(
  function(config) {
    nprogress.start();
    if (store.getters["user/jwt"]) {
      config.headers.common["Authorization"] = `Bearer ${store.getters["user/jwt"]}`;
      config.headers.common["Language"] = store.getters["user/account"].language;
    }

    // TKP-237 t.8: toplu işlemin taslak satırları tek satırda (yalnız işlem süresince)
    const draftBatch = draftBatchHeader();
    if (draftBatch) config.headers["X-Draft-Batch"] = draftBatch;
    // TKP-242 q.1: arka plan işleminde yazma istekleri gönderilmez, kaydedilir (tek istekle sunucuya gider)
    if (shouldRecord(config)) config.adapter = recordingAdapter;

    const deviceId = store.getters["user/deviceId"];
    if (deviceId) {
      config.headers.common["X-Device-Id"] = deviceId;
    }

    const selectedWorkspace = store.getters["switchWorkspace/selected"];
    if (selectedWorkspace) {
      config.headers.common["Workspace"] = selectedWorkspace._id;
    }

    const selectedOrganization = store.getters["organization/selected"];
    if (selectedOrganization) {
      config.headers.common["Organization"] = selectedOrganization._id;
      config.headers.common["Product"] = store.getters["product/currentProduct"];
    }

    return config;
  },
  (error) => {
    nprogress.done();
    Promise.reject(error);
  }
);

setBatchDraftedHandler(() => store.dispatch("approval/fetch")); // TKP-241 p.4: toplu işlem bitince taslaklar bir kez

axios.interceptors.response.use(
  (response) => {
    nprogress.done();
    const { status, request } = response;
    // TKP-104 / md. 13: onay açıkken onay yetkisi olmayan kullanıcının yazması taslağa gider ({draft:true, edit}).
    // Bugünkü akışlar kayıt bekler: yanıt kayıt biçimine çevrilir, kullanıcı bilgilendirilir, rozetler için taslaklar yenilenir.
    if (response?.data?.draft === true && response.data.edit) {
      response.data = entityFromDraftResponse(response.data);
      // TKP-241 p.4: toplu işlemde (batch) ara bildirim ve yenileme yok — işlem sonunda kendi özetini verir
      if (!draftBatchHeader()) {
        new Vue().$toast.info(i18n.t("approval.saved-to-draft"));
        noteDraft();
        store.dispatch("approval/fetch").catch(() => undefined);
      } else markBatchDrafted();
      return response;
    }
    if (status === 204 && !request.responseURL.includes("activation") && !request.responseURL.includes("forgot-password")) {
      const app = new Vue();
      app.$toast.error(i18n.t("http.204"));
      return Promise.reject(response);
    }
    return response;
  },
  async function(error) {
    nprogress.done();
    const { response } = error;
    const originalRequest = error.config;

    if (response) {
      const status = response.status;

      // 403 gövdesi düz metin gelir (ör. md. 15 kilit mesajı); hata işleyicisi `data.message` okur
      if (status === 403 && typeof response.data === "string" && response.data && response.data !== "Forbidden") {
        response.data = { message: response.data };
      }

      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/login") &&
        !originalRequest.url?.includes("/refresh-token") &&
        !originalRequest.url?.includes("/logout")
      ) {
        originalRequest._retry = true;
        try {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = store.dispatch("user/refreshToken");
          }

          await refreshPromise;
          const latestJwt = store.getters["user/jwt"];
          if (latestJwt) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = `Bearer ${latestJwt}`;
          }
          return axios(originalRequest);
        } catch (refreshError) {
          if (window.location.pathname != "/login") {
            store.dispatch("user/logOut");
          }
          return Promise.reject(refreshError?.response || refreshError);
        } finally {
          if (isRefreshing) {
            isRefreshing = false;
            refreshPromise = null;
          }
        }
      }

      if (status === 401) {
        setTimeout(() => {
          if (window.location.pathname != "/login") {
            store.dispatch("user/logOut");
          }
        }, 1000);
      }
    }

    return Promise.reject(response || error);
  }
);
export default axios;
